// Draw-a-signature pad (mouse, touch, pen). Exports a 600×200 PNG data URL
// that the backend validates (see docs/CLIENT-SIGNATURE-SPEC.md). The pad is
// intentionally dumb: no smoothing libraries, no external deps.
import { Eraser } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const SIGNATURE_W = 600;
export const SIGNATURE_H = 200;

export interface SignaturePadProps {
  /** Called with the PNG data URL after every stroke, or null when cleared/empty. */
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
  label?: string;
  /** Faint guide text shown inside the pad until the first stroke. */
  hint?: string;
}

export function SignaturePad({ onChange, disabled, label = "Draw your signature", hint = "Sign here with your finger or mouse" }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const cancelled = useRef(false);
  const strokes = useRef(0);
  const [empty, setEmpty] = useState(true);

  // A transparent canvas with a 2px black pen. We paint on the backing store at
  // the fixed export size and let CSS scale it, so the exported PNG is always
  // 600×200 no matter how wide the phone is.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = SIGNATURE_W;
    c.height = SIGNATURE_H;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111";
  }, []);

  function pos(e: PointerEvent | React.PointerEvent): { x: number; y: number } {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / r.width) * SIGNATURE_W,
      y: ((e.clientY - r.top) / r.height) * SIGNATURE_H,
    };
  }

  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    e.preventDefault();
    const c = canvasRef.current!;
    c.setPointerCapture(e.pointerId);
    const ctx = c.getContext("2d")!;
    const p = pos(e);
    drawing.current = true;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    // A tap with no movement still leaves a dot.
    ctx.lineTo(p.x + 0.1, p.y + 0.1);
    ctx.stroke();
  }

  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || cancelled.current || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  }

  function up(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current = false;
    // A stroke that was in progress when Clear was pressed is cancelled, never exported.
    if (cancelled.current) { cancelled.current = false; return; }
    strokes.current += 1;
    setEmpty(false);
    try {
      canvasRef.current!.releasePointerCapture(e.pointerId);
    } catch { /* already released */ }
    onChange(canvasRef.current!.toDataURL("image/png"));
  }

  function clear() {
    const c = canvasRef.current;
    if (!c) return;
    // Cancel any stroke still in progress so its pointer-up cannot export the
    // (now empty) canvas as a signature.
    if (drawing.current) { cancelled.current = true; drawing.current = false; }
    const ctx = c.getContext("2d")!;
    ctx.beginPath();
    ctx.clearRect(0, 0, SIGNATURE_W, SIGNATURE_H);
    strokes.current = 0;
    setEmpty(true);
    onChange(null);
  }

  return (
    <div className="signature-pad" aria-disabled={disabled || undefined}>
      <div className="signature-pad__head">
        <span className="field-label" style={{ margin: 0 }}>{label}</span>
        <button type="button" className="ghost-button signature-pad__clear" onClick={clear} disabled={disabled || empty}>
          <Eraser size={13} aria-hidden="true" /> Clear
        </button>
      </div>
      <div className="signature-pad__frame">
        <canvas
          ref={canvasRef}
          className="signature-pad__canvas"
          role="img"
          aria-label={empty ? "Signature pad, empty" : "Signature pad with your drawn signature"}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          onPointerLeave={(e) => { if (drawing.current) up(e); }}
        />
        {empty && <span className="signature-pad__hint" aria-hidden="true">{hint}</span>}
        <span className="signature-pad__baseline" aria-hidden="true" />
      </div>
    </div>
  );
}
